import React, { useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { verifyBetaCode } from "../services/supabaseClient";
import BetaCodeModal from "./BetaCodeModal";
import WaitlistModal from "./WaitlistModal";

const AuthState = {
  SIGN_IN: "sign_in",
  SIGN_UP: "sign_up",
  RESET_PASSWORD: "reset_password",
  BETA_CODE: "beta_code",
  WAITLIST: "waitlist",
};

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [authState, setAuthState] = useState(AuthState.SIGN_IN);

  const navigate = useNavigate();
  const {
    user,
    resetPasswordForEmail,
    signIn,
    signUp,
    signOut,
    signInWithAzure,
  } = useAuth();

  const handleAuth = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsLoading(true);

    if (authState === AuthState.RESET_PASSWORD) {
      resetPasswordForEmail(email)
        .then(() => {
          const message = "Email to reset password sent!";
          toast.success(message);
        })
        .catch((error) => {
          console.error("[Auth] Password reset failed:", {
            error,
            name: error.name,
            message: error.message,
            stack: error.stack,
          });
          setMessage(error.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
      return;
    }

    try {
      const authFunction = authState === AuthState.SIGN_UP ? signUp : signIn;
      const response = await authFunction({ email, password });
      if (response.error) {
        throw response.error;
      }

      if (!response.user) {
        throw new Error("[Auth] Authenticaton failed: No user returned");
      }
      // TODO setup user (currently case and firm query throw errors)
      navigate("/");
    } catch (error) {
      console.error("[Auth] Authenticaton failed:", {
        error,
        name: error.name,
        message: error.message,
        stack: error.stack,
      });
      setMessage(error.message);
      setAuthState(AuthState.SIGN_IN);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      if (error) throw error;
      setMessage("Signed out successfully!");
    } catch (error) {
      console.error("Error signing out:", error.message);
      setMessage(error.message);
    }
  };

  // const handleMagicLinkSignIn = async () => {
  //   setIsLoading(true);
  //   setMessage('');
  //   try {
  //     const { error } = await signInWithMagicLink(email);
  //     if (error) throw error;
  //     setMessage('Check your email for the magic link!');
  //   } catch (error) {
  //     console.error('Error sending magic link:', error.message);
  //     setMessage(error.message);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleMicrosoftSignIn = async () => {
    setIsLoading(true);
    try {
      const { error } = await signInWithAzure();
      if (error) throw error;
      // The user will be redirected to Microsoft for authentication
    } catch (error) {
      setMessage(error.message);
      setAuthState(AuthState.SIGN_IN);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBetaCodeSubmit = async (code) => {
    setIsLoading(true);
    const isValidCode = await verifyBetaCode(code);
    if (isValidCode) {
      setMessage(""); // Clear any previous error messages
      setAuthState(AuthState.SIGN_UP);
    } else {
      setMessage("Invalid beta code. Please try again.");
    }
    setIsLoading(false);
  };

  if (user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
          <span className="block mb-4">Signed in as: {user.email}</span>
          <button
            onClick={handleSignOut}
            className="w-full bg-red-500 text-white px-4 py-2 rounded-md hover:bg-red-600 transition duration-300"
          >
            Sign Out
          </button>
          {message && <p className="mt-4 text-sm text-green-600">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md">
        {authState === AuthState.BETA_CODE ? (
          <BetaCodeModal
            onSubmit={handleBetaCodeSubmit}
            onClose={() => setAuthState(AuthState.SIGN_IN)}
            onWaitlistClick={() => {
              setAuthState(AuthState.WAITLIST);
            }}
            errorMessage={message} // Pass the error message to the modal
          />
        ) : authState === AuthState.WAITLIST ? (
          <WaitlistModal onClose={() => setAuthState(AuthState.SIGN_IN)} />
        ) : (
          <div className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4">
            <div className="flex items-center mb-6">
              <img
                src="/logo_dark.png"
                alt="Paribus AI Logo"
                className="h-8 mr-4"
              />
              <h1 className="text-2xl font-bold text-green-800">Paribus AI</h1>
            </div>
            {authState === AuthState.SIGN_UP && (
              <p className="mb-4 text-sm text-gray-600">
                You're in! Enter your email and a password below.
              </p>
            )}
            <form onSubmit={handleAuth}>
              <div className="mb-4">
                <input
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              {authState !== AuthState.RESET_PASSWORD && (
                <div className="mb-6">
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-none focus:shadow-outline"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              )}
              {message && (
                <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                  {message}
                </div>
              )}
              <div className="flex items-center justify-between mb-6">
                <button
                  className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                    isLoading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading
                    ? "Processing..."
                    : authState === AuthState.SIGN_UP
                    ? "Sign Up"
                    : authState === AuthState.RESET_PASSWORD
                    ? "Reset Password"
                    : "Sign In"}
                </button>
                {authState !== AuthState.RESET_PASSWORD ? (
                  <a
                    className="inline-block align-baseline font-bold text-sm text-green-500 hover:text-green-800"
                    href="#"
                    onClick={() =>
                      setAuthState(
                        authState === AuthState.SIGN_UP
                          ? AuthState.SIGN_IN
                          : AuthState.BETA_CODE
                      )
                    }
                  >
                    {authState === AuthState.SIGN_UP
                      ? "Already have an account? Sign In"
                      : "Have a beta code? Sign Up"}
                  </a>
                ) : (
                  <a
                    className="inline-block align-baseline font-bold text-sm text-green-500 hover:text-green-800"
                    href="#"
                    onClick={() => {
                      setAuthState(AuthState.SIGN_IN);
                    }}
                  >
                    Go back to Sign In
                  </a>
                )}
              </div>
            </form>
            {authState === AuthState.SIGN_IN && (
              <>
                <div className="flex flex-col space-y-4 mt-6">
                  <button
                    onClick={handleMicrosoftSignIn}
                    className="flex items-center justify-center bg-black hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded shadow"
                    disabled={isLoading}
                  >
                    <img
                      src="/microsoft-logo.jpg"
                      alt="Microsoft logo"
                      className="w-6 h-6 mr-2"
                    />
                    Sign in with Microsoft
                  </button>
                </div>
                <div className="text-center mt-4">
                  <a
                    className="inline-block align-baseline font-bold text-sm text-green-500 hover:text-green-800"
                    href="#"
                    onClick={() => {
                      setAuthState(AuthState.RESET_PASSWORD);
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;
